import { z } from 'zod';
import { platformApprovedActionProviderDescriptorV2Schema, platformApprovedActionProviderRequestV2Schema, platformApprovedActionProviderResultV2Schema, platformApprovedActionProviderSlot, platformReceiptBoundActionProviderHostCapability, } from '@crest/platform-contracts/hosts';
import { projectsCountryCodeSchema, projectsDateSchema, projectsPriorityKeySchema, projectsProjectTypeKeySchema, projectsPropertyTypeKeySchema, projectsStatusKeySchema, } from './projects-project.contract.js';
export const projectsAssistantActionProviderId = 'projects.assistant-actions';
export const projectsAssistantActionProviderCapability = 'projects.assistant-actions.v1';
export const projectsAssistantActionSlot = platformApprovedActionProviderSlot;
/**
 * Frozen input schema for the accepted `projects.project-update.v1` action.
 *
 * This is deliberately NOT the shared `projectsUpdateProjectSchema`: v1
 * approvals were created and hashed against exactly this field set, so its
 * shape must never change. New Project fields belong to the v2 action below.
 * The drift regression in `projects-assistant-actions.contract.test.ts` pins
 * this shape by canonical JSON-Schema fingerprint.
 */
export const projectsAssistantProjectUpdateV1InputSchema = z.strictObject({
    name: z.string().trim().min(1).max(240).optional(),
    status: projectsStatusKeySchema.optional(),
    organizationId: z.string().uuid().nullable().optional(),
    primaryContactId: z.string().uuid().nullable().optional(),
    customerDisplayName: z.string().trim().max(240).nullable().optional(),
    customerPhone: z.string().trim().max(80).nullable().optional(),
    customerEmail: z.string().trim().email().max(320).nullable().optional(),
    customerAddressLine1: z.string().trim().max(240).nullable().optional(),
    customerAddressLine2: z.string().trim().max(240).nullable().optional(),
    customerCity: z.string().trim().max(120).nullable().optional(),
    customerRegion: z.string().trim().max(120).nullable().optional(),
    customerPostalCode: z.string().trim().max(40).nullable().optional(),
    customerCountryCode: projectsCountryCodeSchema.nullable().optional(),
    addressLine1: z.string().trim().max(240).nullable().optional(),
    addressLine2: z.string().trim().max(240).nullable().optional(),
    city: z.string().trim().max(120).nullable().optional(),
    region: z.string().trim().max(120).nullable().optional(),
    postalCode: z.string().trim().max(40).nullable().optional(),
    countryCode: projectsCountryCodeSchema.optional(),
    expectedRevision: z.number().int().positive(),
}).refine((value) => Object.keys(value).some((key) => key !== 'expectedRevision'), 'At least one project field must be changed.');
export const projectsAssistantProjectUpdateActionId = 'projects.project-update.v1';
/**
 * The accepted, opaque v1 wire schema hash. Approvals created before this
 * batch stored this exact value; it is frozen and is never recomputed.
 */
export const projectsAssistantProjectUpdateInputSchemaHash = '1b0aa1be8a1dd4dcbf26deed268d577d0c2e1359edf1ad247fecb62147c9eeae';
/**
 * Versioned v2 action input: the frozen v1 fields plus the Projects-owned
 * operational fields (type, priority, property type, water-job designation,
 * start and target completion dates). There is deliberately no business-entity
 * field: tenant identity is the business boundary.
 */
export const projectsAssistantProjectUpdateV2InputSchema = z.strictObject({
    name: z.string().trim().min(1).max(240).optional(),
    status: projectsStatusKeySchema.optional(),
    projectType: projectsProjectTypeKeySchema.optional(),
    priority: projectsPriorityKeySchema.optional(),
    propertyType: projectsPropertyTypeKeySchema.nullable().optional(),
    isWaterJob: z.boolean().optional(),
    startDate: projectsDateSchema.nullable().optional(),
    targetCompletionDate: projectsDateSchema.nullable().optional(),
    organizationId: z.string().uuid().nullable().optional(),
    primaryContactId: z.string().uuid().nullable().optional(),
    customerDisplayName: z.string().trim().max(240).nullable().optional(),
    customerPhone: z.string().trim().max(80).nullable().optional(),
    customerEmail: z.string().trim().email().max(320).nullable().optional(),
    customerAddressLine1: z.string().trim().max(240).nullable().optional(),
    customerAddressLine2: z.string().trim().max(240).nullable().optional(),
    customerCity: z.string().trim().max(120).nullable().optional(),
    customerRegion: z.string().trim().max(120).nullable().optional(),
    customerPostalCode: z.string().trim().max(40).nullable().optional(),
    customerCountryCode: projectsCountryCodeSchema.nullable().optional(),
    addressLine1: z.string().trim().max(240).nullable().optional(),
    addressLine2: z.string().trim().max(240).nullable().optional(),
    city: z.string().trim().max(120).nullable().optional(),
    region: z.string().trim().max(120).nullable().optional(),
    postalCode: z.string().trim().max(40).nullable().optional(),
    countryCode: projectsCountryCodeSchema.optional(),
    expectedRevision: z.number().int().positive(),
}).refine((value) => Object.keys(value).some((key) => key !== 'expectedRevision'), 'At least one project field must be changed.');
export const projectsAssistantProjectUpdateV2ActionId = 'projects.project-update.v2';
/**
 * sha256 of the canonical JSON encoding of the v2 input schema's JSON-Schema
 * artifact. Recomputed by the contract test; changing the v2 schema without
 * declaring a new version/hash fails that regression.
 */
export const projectsAssistantProjectUpdateV2InputSchemaHash = 'f59e95646543275109bfe1929c4458a9957dbd186cd94fc054326897a5eee0fe';
/**
 * Owner-specific restoration for one exact Assistant Project-update
 * execution. Projects derives the prior values from its private immutable
 * origin evidence; callers cannot submit an arbitrary inverse patch.
 */
export const projectsAssistantProjectUpdateRestorationActionId = 'projects.restore-project-update.v1';
export const projectsAssistantProjectUpdateRestorationInputSchema = z.strictObject({
    targetProjectId: z.string().uuid(),
    originalExecutionId: z.string().uuid(),
    // Project revisions are PostgreSQL int4 and the mutation increments once.
    expectedRevision: z.number().int().positive().max(2_147_483_646),
    reason: z.string().trim().min(5).max(2_000),
    restorationSummary: z.string().trim().min(1).max(500),
});
export const projectsAssistantProjectUpdateRestorationInputSchemaHash = '26ac97cd17224696e01e5e2466837fbe3f3623edd4601582269ff9c0453a4d5e';
const projectsAssistantAddressProvenanceSchema = z.discriminatedUnion('kind', [
    z.strictObject({
        kind: z.literal('immutable-user-message'),
        messageId: z.string().uuid(),
        messageContentHash: z.string().regex(/^[a-f0-9]{64}$/),
    }),
    z.strictObject({
        kind: z.literal('accessible-relationships-context'),
        contextResourceType: z.literal('relationships.contact'),
        contextResourceId: z.string().uuid(),
        contextContentHash: z.string().regex(/^[a-f0-9]{64}$/),
    }),
]);
const projectsAssistantProjectCreateCommonInputFields = {
    name: z.string().trim().min(1).max(240),
    status: projectsStatusKeySchema.default('new'),
    priority: projectsPriorityKeySchema.default('normal'),
    propertyType: projectsPropertyTypeKeySchema.nullable().default(null),
    startDate: projectsDateSchema.nullable().default(null),
    targetCompletionDate: projectsDateSchema.nullable().default(null),
    primaryContactId: z.string().uuid().nullable().default(null),
    customerDisplayName: z.string().trim().max(240).nullable().default(null),
    customerPhone: z.string().trim().max(80).nullable().default(null),
    customerEmail: z.string().trim().email().max(320).nullable().default(null),
    customerAddressLine1: z.string().trim().max(240).nullable().default(null),
    customerAddressLine2: z.string().trim().max(240).nullable().default(null),
    customerCity: z.string().trim().max(120).nullable().default(null),
    customerRegion: z.string().trim().max(120).nullable().default(null),
    customerPostalCode: z.string().trim().max(40).nullable().default(null),
    customerCountryCode: projectsCountryCodeSchema.nullable().default(null),
    addressLine1: z.string().trim().min(1).max(240),
    addressLine2: z.string().trim().max(240).nullable().default(null),
    city: z.string().trim().max(120).nullable().default(null),
    region: z.string().trim().max(120).nullable().default(null),
    postalCode: z.string().trim().max(40).nullable().default(null),
    countryCode: projectsCountryCodeSchema.default('US'),
    intakeNote: z.string().trim().min(1).max(20_000).optional(),
    addressProvenance: projectsAssistantAddressProvenanceSchema,
};
/**
 * Receipt-bound Projects create. The legacy translator resolves a trustworthy
 * name and address provenance before proposal creation. Projects still owns
 * every persisted field, server Project number, creator assignment, optional
 * intake note, immutable origin, and owner readback.
 */
export const projectsAssistantProjectCreateActionId = 'projects.project-create.v1';
export const projectsAssistantProjectCreateInputSchema = z.union([
    z.strictObject({
        ...projectsAssistantProjectCreateCommonInputFields,
        projectType: z.literal('mitigation').default('mitigation'),
        isWaterJob: z.boolean().default(true),
    }),
    z.strictObject({
        ...projectsAssistantProjectCreateCommonInputFields,
        projectType: projectsProjectTypeKeySchema.refine((projectType) => projectType !== 'mitigation', 'Non-mitigation Project type expected.'),
        isWaterJob: z.boolean().default(false),
    }),
]).superRefine((project, context) => {
    if (project.startDate !== null &&
        project.targetCompletionDate !== null &&
        project.targetCompletionDate < project.startDate) {
        context.addIssue({
            code: 'custom',
            path: ['targetCompletionDate'],
            message: 'Target completion date cannot be before the start date.',
        });
    }
});
export const projectsAssistantProjectCreateInputSchemaHash = 'c5e17bed31d892aa79e172348008f5c1d3d5a9983f1e218795e24ee3d7c4b661';
/**
 * Non-destructive compensation for one exact still-current Assistant-created
 * Project. The owner derives the source Project from immutable origin; callers
 * cannot submit a delete, cancellation, status transition, or inverse payload.
 */
export const projectsAssistantArchiveCreatedProjectActionId = 'projects.archive-assistant-created-project.v1';
export const projectsAssistantArchiveCreatedProjectInputSchema = z.strictObject({
    targetProjectId: z.string().uuid(),
    originalExecutionId: z.string().uuid(),
    expectedRevision: z.number().int().positive().max(2_147_483_646),
    reason: z.string().trim().min(5).max(2_000),
    compensationSummary: z.string().trim().min(1).max(500),
});
export const projectsAssistantArchiveCreatedProjectInputSchemaHash = '07657754ff7ace715f68949d70e0a334e019cb3c4468793267f452b756695d61';
export const projectsAssistantActionProviderDescriptor = platformApprovedActionProviderDescriptorV2Schema.parse({
    kind: 'descriptor',
    contractVersion: platformReceiptBoundActionProviderHostCapability,
    providerId: projectsAssistantActionProviderId,
    providerCapability: projectsAssistantActionProviderCapability,
    displayName: 'Projects approved actions',
    actions: [{
            actionId: projectsAssistantProjectUpdateActionId,
            displayName: 'Update Project',
            targetResourceType: 'projects.project',
            inputSchemaHash: projectsAssistantProjectUpdateInputSchemaHash,
            requiredPermission: 'projects.projects.update',
            // Truthful owner safety declaration: a Project update supersedes the
            // previous field values under optimistic revision checking. No owner
            // The registered action is owner-specific and derives its inverse from
            // immutable Projects evidence; it is not a generic rollback.
            assistantPolicy: {
                policyContractVersion: 'platform.assistant-action-policy.v1',
                confirmation: 'explicit',
                effectClass: 'superseding-write',
                externalEffectClass: 'none',
                reversal: {
                    kind: 'superseding-write',
                    registeredReversalActionId: projectsAssistantProjectUpdateRestorationActionId,
                },
                ownerReadbackRequired: true,
                inputSchemaHash: projectsAssistantProjectUpdateInputSchemaHash,
            },
        }, {
            actionId: projectsAssistantProjectUpdateV2ActionId,
            displayName: 'Update Project',
            targetResourceType: 'projects.project',
            inputSchemaHash: projectsAssistantProjectUpdateV2InputSchemaHash,
            requiredPermission: 'projects.projects.update',
            assistantPolicy: {
                policyContractVersion: 'platform.assistant-action-policy.v1',
                confirmation: 'explicit',
                effectClass: 'superseding-write',
                externalEffectClass: 'none',
                reversal: {
                    kind: 'superseding-write',
                    registeredReversalActionId: projectsAssistantProjectUpdateRestorationActionId,
                },
                ownerReadbackRequired: true,
                inputSchemaHash: projectsAssistantProjectUpdateV2InputSchemaHash,
            },
        }, {
            actionId: projectsAssistantProjectUpdateRestorationActionId,
            displayName: 'Restore prior Project update values',
            targetResourceType: 'projects.project',
            inputSchemaHash: projectsAssistantProjectUpdateRestorationInputSchemaHash,
            requiredPermission: 'projects.projects.update',
            assistantPolicy: {
                policyContractVersion: 'platform.assistant-action-policy.v1',
                confirmation: 'explicit',
                effectClass: 'superseding-write',
                externalEffectClass: 'none',
                // A restoration is itself a superseding Project update. There is no
                // recursive or generic reversal registered for it.
                reversal: { kind: 'superseding-write', registeredReversalActionId: null },
                ownerReadbackRequired: true,
                inputSchemaHash: projectsAssistantProjectUpdateRestorationInputSchemaHash,
            },
        }, {
            actionId: projectsAssistantProjectCreateActionId,
            displayName: 'Create Project',
            targetResourceType: 'projects.project',
            inputSchemaHash: projectsAssistantProjectCreateInputSchemaHash,
            requiredPermission: 'projects.projects.create',
            assistantPolicy: {
                policyContractVersion: 'platform.assistant-action-policy.v1',
                confirmation: 'explicit',
                effectClass: 'compensating-write',
                externalEffectClass: 'none',
                reversal: {
                    kind: 'compensating-action',
                    registeredReversalActionId: null,
                },
                ownerReadbackRequired: true,
                inputSchemaHash: projectsAssistantProjectCreateInputSchemaHash,
            },
        }],
});
export const projectsAssistantActionProviderRequestSchema = platformApprovedActionProviderRequestV2Schema;
export const projectsAssistantActionProviderResultSchema = platformApprovedActionProviderResultV2Schema;
//# sourceMappingURL=projects-assistant-actions.contract.js.map